import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Toast from '../components/Toast';
import { CATEGORY_LABELS } from '../constants';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import './ExerciseDetail.css';

export default function ExerciseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [exercise, setExercise] = useState(null);
  const [history,  setHistory]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(false);
  const [editing,  setEditing]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [toast,    setToast]    = useState(null);
  const [form,     setForm]     = useState({
    name: '', category: '', muscle_group: '', description: '', is_global: false, is_bodyweight: false,
  });

  const showToast = (msg, type = 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [exRes, histRes] = await Promise.all([
        api.get(`/exercises/${id}`),
        api.get(`/exercises/${id}/history`).catch(() => ({ data: {} })),
      ]);
      const exData = exRes.data.exercise || exRes.data;
      setExercise(exData);
      setForm({
        name:         exData.name || '',
        category:     exData.category || '',
        muscle_group: exData.muscle_group || '',
        description:  exData.description || '',
        is_global:    exData.is_global || false,
        is_bodyweight: exData.is_bodyweight || false,
      });
      // history endpoint returns flat array of sets grouped by date
      const rawHistory = histRes.data.history || [];
      const byDate = {};
      rawHistory.forEach(s => {
        if (!byDate[s.date]) byDate[s.date] = { date: s.date, sets: [], max_weight: 0 };
        byDate[s.date].sets.push(s);
        if ((s.weight || 0) > byDate[s.date].max_weight) byDate[s.date].max_weight = s.weight || 0;
      });
      const sorted = Object.values(byDate).sort((a, b) => b.date.localeCompare(a.date));
      setHistory(sorted.slice(0, 30));
    } catch {
      setError(true);
      showToast('Error al cargar el ejercicio');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  async function handleSave(e) {
    e.preventDefault();
    if (!form.name.trim()) {
      showToast('El nombre del ejercicio es obligatorio');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/exercises/${id}`, { ...form, name: form.name.trim(), category: form.category.toLowerCase() });
      showToast('¡Ejercicio actualizado!', 'success');
      setEditing(false);
      load();
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al actualizar');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <Layout>
        <div className="px-16" style={{ paddingTop: 16 }}>
          <div className="skeleton skeleton-line-xs" />
          <div className="skeleton skeleton-line" style={{ height: 28, marginTop: 12 }} />
          <div className="skeleton skeleton-line-sm" />
          <div style={{ marginTop: 24 }}>
            <div className="skeleton skeleton-card" />
            <div className="skeleton skeleton-card" />
            <div className="skeleton skeleton-card" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!exercise) {
    return (
      <Layout>
        <div className="empty-state">
          <div className="empty-state-icon">{error ? '⚠️' : '❓'}</div>
          <div className="empty-state-title">{error ? 'Error de conexión' : 'Ejercicio no encontrado'}</div>
          <button className="btn btn-secondary btn-sm mt-8" onClick={() => navigate('/exercises')}>
            ← Atrás
          </button>
        </div>
      </Layout>
    );
  }

  function estimate1rm(weight, reps) {
    if (!weight || !reps || reps <= 0) return null;
    if (reps === 1) return weight;
    return Math.round(weight * (1 + reps / 30) * 10) / 10;
  }

  const bestEst1rm = history.reduce((best, entry) => {
    const maxSet = entry.sets.reduce((max, s) => {
      const est = estimate1rm(s.weight, s.reps);
      return est && est > max ? est : max;
    }, 0);
    return maxSet > best ? maxSet : best;
  }, 0);

  const isOwner = exercise ? (exercise.is_owner ?? true) : false;
  const canEdit = isOwner || (exercise?.is_global && user?.is_admin);
  const prWeight = exercise ? (exercise.pr_weight || 0) : 0;
  const prReps = exercise ? (exercise.pr_reps || 0) : 0;
  const isCardio = exercise?.category === 'cardio';
  const isBw = exercise?.is_bodyweight;

  return (
    <Layout>
      {toast && <Toast message={toast.msg} type={toast.type} />}
      <div className="page">
        <div className="page-header">
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/exercises')}>
            ← Atrás
          </button>
          {canEdit && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setEditing(e => !e)}
              type="button"
            >
              {editing ? 'Cancelar' : '✏️ Editar'}
            </button>
          )}
        </div>

        {editing ? (
          <div className="px-16">
            <form onSubmit={handleSave}>
              <div className="form-group">
                <label className="form-label">Nombre</label>
                <input className="form-input" value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">Categoría</label>
                <select className="form-input" value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                  {['chest','back','legs','shoulders','arms','core','cardio','mobility'].map(c => (
                    <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Grupo muscular</label>
                <input className="form-input" value={form.muscle_group}
                  onChange={e => setForm(f => ({ ...f, muscle_group: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Descripción</label>
                <textarea className="form-input" rows={4} value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  style={{ resize: 'none' }} />
              </div>
              {user?.is_admin && (
                <>
                  <div className="form-group" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 0 }}>
                      <input
                        type="checkbox"
                        checked={form.is_global}
                        onChange={e => setForm(f => ({ ...f, is_global: e.target.checked }))}
                        style={{ accentColor: '#e94560', width: 18, height: 18 }}
                      />
                      Ejercicio del admin (disponible para todos)
                    </label>
                  </div>
                  <div className="form-group" style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 0 }}>
                      <input type="checkbox" checked={form.is_bodyweight} onChange={e => setForm(f => ({ ...f, is_bodyweight: e.target.checked }))} style={{ accentColor: '#4caf50', width: 18, height: 18 }} />
                      Sin peso (solo repeticiones)
                    </label>
                  </div>
                </>
              )}
              <button className="btn btn-primary btn-full" type="submit" disabled={saving}>
                {saving ? <span className="spinner spinner-sm" /> : 'Guardar cambios'}
              </button>
            </form>
          </div>
        ) : (
          <>
            <div className="detail-hero px-16">
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span className={`badge badge-${(exercise.category || 'other').toLowerCase()}`}>
                  {CATEGORY_LABELS[exercise.category] || exercise.category || 'Otro'}
                </span>
                {exercise.is_global && (
                  <span className="badge badge-admin">Admin</span>
                )}
                {exercise.is_bodyweight && (
                  <span className="badge badge-admin" style={{ fontSize: '0.7rem', padding: '2px 6px' }}>Sin peso</span>
                )}
              </div>
              {exercise.gif_url && (
                <div className="detail-gif-container">
                  <img src={exercise.gif_url} alt={exercise.name} className="detail-gif" />
                </div>
              )}
              <h2 className="detail-name">{exercise.name}</h2>
              {exercise.muscle_group && (
                <div className="detail-muscle text-muted text-sm">{exercise.muscle_group}</div>
              )}
              {(prWeight > 0 || prReps > 0) && (
                <div className="pr-display">
                  <span className="pr-label">Récord personal</span>
                  <span className="pr-value">
                    🏆 {isCardio ? `${prWeight} min` : isBw ? `${prReps} reps` : `${prWeight} kg`}
                  </span>
                </div>
              )}
              {!isCardio && bestEst1rm > 0 && (
                <div className="est-1rm-display">
                  <span className="pr-label">1RM estimado</span>
                  <span className="est-1rm-value">{bestEst1rm} kg</span>
                </div>
              )}
              {exercise.description && (
                <p className="detail-description text-muted">{exercise.description}</p>
              )}
            </div>

            <div className="px-16 mt-16">
              <h3 className="mb-8 fw-700">Historial reciente</h3>
              {history.length > 1 && (
                <div className="chart-card card mb-16" style={{ padding: 12 }}>
                  <h4 className="chart-title" style={{ fontSize: '0.8rem', marginBottom: 8 }}>Progresión de peso máximo</h4>
                  <ResponsiveContainer width="100%" height={160}>
                    <LineChart data={[...history].reverse().map(h => ({ ...h, label: h.date.substring(5) }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2a2a4a" />
                      <XAxis dataKey="label" stroke="#a0a0b0" fontSize={10} />
                      <YAxis stroke="#a0a0b0" fontSize={10} tickFormatter={v => isCardio ? `${v} min` : `${v}kg`} />
                      <Tooltip contentStyle={{ background: '#1a1a2e', border: '1px solid #2a2a4a', borderRadius: 8, color: '#eaeaea', fontSize: '0.8rem' }} formatter={(value) => isCardio ? [`${value} min`, 'Tiempo máx'] : [`${value} kg`, 'Peso máx']} />
                      <Line type="monotone" dataKey="max_weight" stroke="#e94560" strokeWidth={2} dot={{ fill: '#e94560', r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
              {history.length === 0 ? (
                <div className="empty-state" style={{ padding: '32px 0' }}>
                  <div className="empty-state-icon" style={{ fontSize: '2rem' }}>📋</div>
                  <div className="empty-state-text">Aún no hay historial de entrenamientos.<br />¡Comienza a entrenar!</div>
                </div>
              ) : (
                <div className="history-list">
                  {history.map((entry, i) => (
                    <div key={i} className="history-entry card-secondary mb-8">
                      <div className="flex justify-between items-center mb-8">
                        <span className="text-sm fw-600">
                          {new Date(entry.date).toLocaleDateString('es-ES', {
                            month: 'short', day: 'numeric', year: 'numeric',
                          })}
                        </span>
                        <span className="text-sm text-accent fw-700">Máx: {entry.max_weight}{isCardio ? ' min' : 'kg'}</span>
                      </div>
                      <div className="history-sets">
                        {(entry.sets || []).map((s, si) => (
                                <span key={si} className="history-set-pill">
                                    {exercise.category === 'cardio' ? (
                                      <>{s.weight || s.reps || 0} min</>
                                    ) : exercise.is_bodyweight ? (
                                      <>{s.reps || 0} reps</>
                                    ) : (
                                      <>{s.weight}kg × {s.reps}</>
                                    )}
                                  </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
